/**
 * Offline awareness.
 *
 * The app itself (games, calendar, saved plans already on device) works with no
 * connection thanks to the service worker. Anything that talks to the backend
 * (AI chat, plan generation, podcasts, sync) can't, so we surface a single
 * "No Wifi" popup instead of a silent failure.
 */

export const NO_WIFI_EVENT = "app:no-wifi";

export const isOnline = () =>
  typeof navigator === "undefined" || navigator.onLine !== false;

/** Show the "No Wifi" popup. */
export const showNoWifi = (feature?: string) => {
  window.dispatchEvent(new CustomEvent(NO_WIFI_EVENT, { detail: { feature } }));
};

/**
 * Guard a network-dependent action. Returns false (and pops the dialog) when
 * the device is offline.
 */
export const requireOnline = (feature?: string): boolean => {
  if (isOnline()) return true;
  showNoWifi(feature);
  return false;
};

let installed = false;

/**
 * Global safety net: any fetch attempted while offline fails fast and raises the
 * popup, so every backend call in the app is covered without per-call wiring.
 */
export const installOfflineFetchGuard = () => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.href
          : input.url;

    // Local/precached same-origin assets still resolve from the service worker.
    const isExternal = /^https?:\/\//i.test(url) && !url.startsWith(location.origin);

    if (!isOnline() && isExternal) {
      showNoWifi();
      throw new Error("No Wifi");
    }

    try {
      return await originalFetch(input as RequestInfo, init);
    } catch (err) {
      if (!isOnline() && isExternal) showNoWifi();
      throw err;
    }
  };
};
